import { UploadOutlined } from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd';
import { Alert, Button, Flex, Typography, Upload } from 'antd';
import type { RcFile } from 'antd/es/upload/interface';
import {
  ALLOWED_FILE_EXTENSIONS,
  MAX_UPLOAD_FILESIZE,
  MAX_UPLOAD_FILESIZE_BYTES,
} from 'const/attachments';
import { useUploadAttachmentProps } from 'hooks/attachments.hooks';
import { useDateFormat } from 'hooks/useDateFormat';
import useFilesize from 'hooks/useFilesize';
import useFiletype from 'hooks/useFiletype';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

const { Dragger } = Upload;

function MultiUpload({
  attachments,
  action,
  onFileListChanged,
  itemRender,
  onChange,
}: {
  attachments: UploadFile[];
  action: string;
  onFileListChanged?: () => void;
  itemRender?: UploadProps['itemRender'];
  onChange?: UploadProps['onChange'];
}) {
  const filesize = useFilesize();
  const filetype = useFiletype();
  const format = useDateFormat();
  const { t } = useTranslation();
  const [fileSizeErrors, setFileSizeErrors] = useState<string[]>([]);

  const uploadProps: UploadProps = useUploadAttachmentProps(
    attachments,
    action,
    onFileListChanged,
  );

  useEffect(() => {
    return () => {
      setFileSizeErrors([]);
    };
  }, []);

  // Override the beforeUpload to add file size validation
  const beforeUpload = useCallback(
    (file: RcFile) => {
      const isTooBig = file.size > MAX_UPLOAD_FILESIZE_BYTES;

      if (isTooBig) {
        const errorMessage = t('attachments:file_size_error', {
          filename: file.name,
          maxSize: MAX_UPLOAD_FILESIZE,
        });

        setFileSizeErrors((prev) => [
          ...prev.filter((err) => !err.includes(file.name)),
          errorMessage,
        ]);
        return false;
      } else {
        // Remove any existing error for this file
        setFileSizeErrors((prev) =>
          prev.filter((err) => !err.includes(file.name)),
        );
      }
      return false;
    },
    [t],
  );

  // Enhanced uploadProps with our validation
  const enhancedUploadProps = useMemo(
    () => ({
      ...uploadProps,
      beforeUpload,
    }),
    [uploadProps, beforeUpload],
  );

  const extra = useCallback(
    ({ size = 0, lastModifiedDate = new Date(), name }: UploadFile) => (
      <Typography.Text className="float-right">
        {format(lastModifiedDate, 'PP')} &ndash; {filesize(size)} &ndash;{' '}
        {filetype(name)}
      </Typography.Text>
    ),
    [filesize, filetype, format],
  );

  const showUploadList = useMemo(
    () => ({
      extra,
      showDownloadIcon: false,
      showRemoveIcon: false,
    }),
    [extra],
  );

  return (
    <>
      <Dragger
        {...enhancedUploadProps}
        showUploadList={showUploadList}
        multiple
        className="[&>div]:!bg-white [&>div]:mb-6"
        itemRender={itemRender}
        onChange={onChange}
      >
        <Flex align="center" className="justify-start gap-3 md:justify-between">
          <Button type="primary" ghost icon={<UploadOutlined />}>
            {t('attachments:parcourir')}
          </Button>
          <p className="text-xs mb-0">{t('attachments:aucun_document')}</p>
        </Flex>
        <p className="mt-2 text-left text-xs text-gray-500">
          {t('dossier:upload_file_info', {
            max: MAX_UPLOAD_FILESIZE + ' Mo',
            formats: ALLOWED_FILE_EXTENSIONS.join(', '),
          })}
        </p>
        {fileSizeErrors.length > 0 && (
          <div className="flex flex-col gap-2">
            {fileSizeErrors.map((error, index) => (
              <Alert
                key={index}
                showIcon
                message={error}
                type="error"
                className="bg-transparent border-none text-rouge-copro-10 text-left px-0"
              />
            ))}
          </div>
        )}
      </Dragger>
    </>
  );
}

export default MultiUpload;
